/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
const PICKED_GIFT_ATTRIBUTE = "_bogos_picked";

class Cart extends Core {
	constructor() {
		super();
		this.digitalTypes = ["vouchers", "services"];
		this.container = document.getElementById("cart-drawer");
		this.contents = this.container.querySelector("#cart-drawer-contents");
		this.items = this.container.querySelector("#cart-drawer-items");
		this.itemTemplate = document.querySelector("#cart-drawer-item-template");

		this.discountContainer = document.querySelector("#discount-wrapper");
		this.showDiscountFormBtn = this.discountContainer.querySelector(".discount-opener");
		this.discountForm = this.discountContainer.querySelector(".discount-form");

		this.thresholdNotReached = Array.from(this.container.querySelectorAll("[data-cart-threshold-not-reached]"));
		this.thresholdMinimumShort = Array.from(this.container.querySelectorAll("[data-cart-threshold-minimum-short]"));
		this.thresholdAmount = Array.from(this.container.querySelectorAll("[data-cart-threshold-amount]"));

		this.thresholdPrompt = this.container.querySelector("[data-cart-threshold-prompt]");

		this.checkoutBtn = this.container.querySelector("[data-cart-checkout]");

		this.empty = this.container.querySelector("#cart-drawer-empty");
		this.actions = this.container.querySelector("#cart-drawer-actions");
		this.cartDrawerControllers = Array.from(document.querySelectorAll(`[aria-controls=${this.container.id}]`));
		this.overlay = document.getElementById("cart-overlay");
		this.isExpanded = false;

		this.progressBar = this.container.querySelector(".progress");
		this.milestoneIcon = document.getElementById("milestone-icon");
		this.progressContainer = document.getElementById("progress-container");

		this.upsellContainer = document.getElementById("cart-drawer-upsell");
		this.carouselContainer = document.getElementById("carousel-container");
		this.carouselTemplate = document.getElementById("cart-drawer-carousel-template");
		this.upsellItemTemplate = document.getElementById("cart-drawer-upsell-template");

		this.freeShippingQualified = false;
		this.cartItems = null;

		this.cartUpsell = null;
		this.cartUpsellRules = [
			{
				id: "gifting_low",
				conditions: {
					OR: [{ type: { plants: 1, pots: 1 } }, { type: { bundles: 1 } }],
					valueBelow: 50,
				},
			},
			{
				id: "gifting_high",
				conditions: {
					AND: [{ environment: { plantsIndoor: 1, potsIndoor: 1 } }, { type: { plants: 1, pots: 1, accessories: 1 } }],
					valueAtLeast: 50,
				},
			},
			{
				id: "self_low",
				conditions: {
					indoorShareAbove: 0.51,
					valueBelow: 50,
				},
			},
			{
				id: "self_high",
				conditions: {
					indoorShareAbove: 0.51,
					valueAtLeast: 50,
				},
			},
			{
				id: "outdoor_low",
				conditions: {
					outdoorShareAbove: 0.51,
					valueBelow: 50,
				},
			},
			{
				id: "outdoor_high",
				conditions: {
					outdoorShareAbove: 0.51,
					valueAtLeast: 50,
				},
			},
		];
		this.cartUpsellItems = JSON.parse(this.carouselContainer?.getAttribute("data-cart-upsell-items") || null);
	}

	toggleCartDrawer() {
		this.updateCartContents();
		this.isExpanded = this.container.getAttribute("aria-expanded") === "true";
		this.container.setAttribute("aria-expanded", `${!this.isExpanded}`);
		document.documentElement.classList.toggle("overflow-hidden", !this.isExpanded);

		if (!this.isExpanded) {
			window.dispatchEvent(
				new CustomEvent("cart:cartOpened", { detail: { items: window.cart.cartObject?.lines?.nodes || [] } })
			);
		}
	}

	get minimumOrderValueAchieved() {
		const cart = window?.cart?.cartObject || false;
		if (!cart) return false;

		return this.getCartType() === "digital" ? true : this.getPhysicalSubtotal() >= this.minimumOrderValue;
	}

	updateMinimumOrderThreshold() {
		this.thresholdNotReached.forEach((ele) => {
			ele.toggleAttribute("hidden", this.minimumOrderValueAchieved);
		});
		this.thresholdMinimumShort.forEach((ele) => {
			ele.innerText = Core.formatCurrency(this.minimumOrderValue - this.getPhysicalSubtotal(), false);
		});
		if (!this.minimumOrderValueAchieved) {
			this.checkoutBtn.setAttribute("aria-disabled", true);
			this.checkoutBtn.href = "#";
		} else {
			// &skip_shop_pay=true
			// It's an unofficial solution to avoid the default use of shop pay at checkout,
			// not supported or announced by shopify, so there is no guarantee that it will always work.

			this.checkoutBtn.href = `${window.cart.cartObject.checkoutUrl}&skip_shop_pay=true`;
			this.checkoutBtn.removeAttribute("aria-disabled");
		}
	}

	updateProgressBar() {
		const cartType = this.getCartType();
		if (cartType === "digital") {
			this.progressContainer.classList.add("hidden");
			return;
		}

		const cartSubtotal = this.getPhysicalSubtotal();
		const minOrder = this.minimumOrderValue;
		const freeShipping = this.freeDeliveryThreshold;
		this.progressContainer.classList.remove("hidden");
		this.freeShippingQualified = cartSubtotal >= this.freeDeliveryThreshold;

		let progressWidth;

		if (cartSubtotal < minOrder) {
			progressWidth = (cartSubtotal / minOrder) * 100;
		} else if (cartSubtotal >= minOrder && cartSubtotal < freeShipping) {
			const progress = cartSubtotal - minOrder;
			const range = freeShipping - minOrder;
			progressWidth = 50 + (progress / range) * 50;
		} else if (!this.freeShippingQualified) {
			progressWidth = 100;
		} else {
			progressWidth = 100;
		}

		this.progressBar.style.width = `${progressWidth}%`;
		this.updateProgressText();
		this.updateMilestoneImage();
	}

	updateProgressText() {
		this.messageContainer = document.querySelector(".messages-container");
		this.messageContainer.innerText = "";

		const minOrder = this.minimumOrderValue;
		const freeShipping = this.freeDeliveryThreshold;

		// it uses only physical products subtotal
		const cartTotal = this.getPhysicalSubtotal();
		const cartType = this.getCartType();

		if (cartTotal < minOrder) {
			const msg =
				cartType === "mixed"
					? `Add £${minOrder - cartTotal} to reach our minimum spend on plants, pots and accessories`
					: `Add £${minOrder - cartTotal} to reach our minimum order of £${minOrder}`;
			this.messageContainer.innerText = msg;
			this.progressContainer.classList.remove("hidden", "opacity-0");
			this.progressContainer.classList.add("opacity-100");
			this.contents.classList.remove("pb-[100px]");
			this.contents.classList.add("pb-[150px]");
		} else if (cartTotal >= minOrder && cartTotal < freeShipping) {
			const msg =
				cartType === "mixed"
					? `Add £${freeShipping - cartTotal} to get free standard delivery on plants, pots and accessories`
					: `Add £${freeShipping - cartTotal} to get free standard delivery`;
			this.messageContainer.innerText = msg;
			this.progressContainer.classList.remove("hidden", "opacity-0");
			this.progressContainer.classList.add("opacity-100");
			this.contents.classList.add("pb-[150px]");
			this.contents.classList.remove("pb-[100px]");
		} else {
			this.messageContainer.innerText = "Great news! You qualify for free standard delivery.";

			setTimeout(() => {
				this.progressContainer.classList.remove("opacity-100");
				this.progressContainer.classList.add("opacity-0");

				setTimeout(() => {
					this.progressContainer.classList.add("hidden");
				}, 500);

				this.contents.classList.remove("pb-[150px]");
				this.contents.classList.add("pb-[100px]");
			}, 1000);
		}
	}

	updateMilestoneImage() {
		const cartTotal = this.getPhysicalSubtotal();
		const minOrder = this.minimumOrderValue;

		this.milestoneIcon.classList.remove(
			"top-[-40%]",
			"-right-[-0.9rem]",
			"-translate-y-1/2",
			"top-[-100%]",
			"right-[-1.3rem]",
			"-translate-y-[40%]"
		);

		if (cartTotal < minOrder) {
			this.milestoneIcon.src = "../../assets/images/plant.png";
			this.milestoneIcon.alt = "plant";
			this.milestoneIcon.width = 25;
			this.milestoneIcon.height = 25;

			this.milestoneIcon.classList.add("top-[-100%]", "right-[-0.9rem]", "-translate-y-1/2");
		} else {
			this.milestoneIcon.src = "../../assets/images/van.png";
			this.milestoneIcon.alt = "van";
			this.milestoneIcon.width = 40;
			this.milestoneIcon.height = 40;

			this.milestoneIcon.classList.add("top-[-40%]", "right-[-1.3rem]", "-translate-y-[40%]");
		}
	}

	async updateCartContents() {
		const cartSize = window?.cart?.cartSize || 0;
		this.contents.classList.toggle("hidden", cartSize === 0);
		this.empty.classList.toggle("hidden", cartSize !== 0);
		this.actions.classList.toggle("hidden", cartSize === 0);

		this.showDiscountFormBtn.classList.remove("hidden");
		this.discountForm.classList.add("hidden");
		this.items.innerHTML = "";

		if (window.cart.cartObject && window.cart.cartObject.lines.nodes) {
			window.cart.cartObject.lines.nodes.forEach(this.renderCartItem.bind(this));
			this.cartItems = window.cart.cartObject.lines.nodes;
		}

		this.updateSubtotal();
		this.updateMinimumOrderThreshold();
		this.updateMinimumOrderValues();
		this.updateTotal();
		this.updateProgressBar();
		this.updateCartUpsell();
		this.updateGiftChoices();
	}

	// BOGOS gift offers where the shopper picks the gift, asked for whenever the bag is drawn
	async updateGiftChoices() {
		const section = document.getElementById("cart-drawer-gifts");
		if (!section) return;

		const { cartId } = window.cart;
		if (!cartId || !window.cart.cartSize) {
			section.hidden = true;
			return;
		}

		const request = await this.asyncRequest({
			url: `${this.apiUrl}/cart/bogos-gifts?cartId=${encodeURIComponent(cartId)}&site=${this.siteId}`,
		});
		// The bag may have changed while waiting
		if (!request || request.status !== 200 || window.cart.cartId !== cartId) return;

		const { giftChoices = [] } = JSON.parse(request.response);
		const container = section.querySelector("[data-cart-gift-choices]");
		container.innerHTML = "";
		section.hidden = giftChoices.length === 0;
		if (!giftChoices.length) return;

		section.querySelector("[data-cart-gifts-heading]").innerText = giftChoices.every((gift) => gift.price === 0)
			? "Choose your free gift"
			: "Choose your gift";

		// Same carousel as the "You might also love" upsell
		const carousel = this.carouselTemplate.content.cloneNode(true);
		const carouselEle = carousel.querySelector("[data-carousel]");
		const items = carousel.querySelector("[data-movable]");
		giftChoices.forEach((gift) => items.appendChild(this.renderGiftChoice(gift)));
		container.appendChild(carousel);

		if (carouselEle && typeof Carousel !== "undefined") {
			// eslint-disable-next-line no-new
			new Carousel(carouselEle, { autoAdvance: false, playOnce: false, loop: false, moveScreen: true });
		}
	}

	renderGiftChoice(gift) {
		const node = document.getElementById("cart-drawer-gift-template").content.cloneNode(true);
		const image = node.querySelector("[data-cart-gift-image]");
		image.setAttribute("src", gift.image);
		image.setAttribute("alt", gift.title);
		node.querySelector("[data-cart-gift-title]").innerText = gift.title;
		node.querySelector("[data-cart-gift-variant]").innerText = gift.variantTitle === "Default Title" ? "" : gift.variantTitle;
		node.querySelector("[data-cart-gift-price]").innerText = gift.price > 0 ? Core.formatCurrency(gift.price) : "Free";
		if (gift.originalPrice > gift.price) {
			node.querySelector("[data-cart-gift-original-price]").innerText = Core.formatCurrency(gift.originalPrice);
		}

		node.querySelector("[data-cart-gift-add]").addEventListener("click", async (e) => {
			e.currentTarget.disabled = true;
			// Marks it as picked so the bag lets the shopper remove it and pick another
			const attributes = [...gift.attributes, { key: PICKED_GIFT_ATTRIBUTE, value: "true" }];
			const items = [{ merchandiseId: gift.variantId, quantity: 1, attributes }];
			const previousCart = structuredClone(window.cart);
			try {
				const cart = await this.addItemsCart(e, { site: this.siteId, cartId: window.cart.cartId, items });
				if (cart?.lines) this.showMessage(cart, previousCart, items);
			} catch (error) {
				this.messages.addMessage(["Sorry, we couldn't add your gift"], "error");
			}
			this.updateCartContents();
		});
		return node;
	}

	updateSubtotal() {
		const deliveryPriceEle = this.container.querySelector("[data-cart-delivery-price]");
		const subtotalEle = this.container.querySelector("[data-cart-subtotal]");
		subtotalEle.innerText = Core.formatCurrency(window.cart.subtotal);

		// Does not show delivery fee for cart with only vouchers
		if (this.getCartType() === "digital") {
			deliveryPriceEle.parentElement.classList.add("hidden");
			this.deliveryPrice = 0;
			return;
		}
		deliveryPriceEle.parentElement.classList.remove("hidden");

		// Once Shopify has resolved a real delivery option (e.g. after visiting checkout), its cost is
		// already included in window.cart.total - display it, but don't add it again in updateTotal().
		const deliveryGroups = window.cart.cartObject?.deliveryGroups?.nodes || [];
		const resolvedDeliveryGroups = deliveryGroups.filter((group) => group.selectedDeliveryOption);
		const resolvedDeliveryPrice = resolvedDeliveryGroups.reduce(
			(total, group) => total + parseFloat(group.selectedDeliveryOption.estimatedCost.amount),
			0
		);

		// Groups without a selected option yet aren't reflected in window.cart.total, so they still
		// need the flat estimate - otherwise a partially-resolved cart understates its total.
		const allGroupsResolved = deliveryGroups.length > 0 && resolvedDeliveryGroups.length === deliveryGroups.length;
		this.deliveryPrice = allGroupsResolved
			? 0
			: this.getPhysicalSubtotal() >= this.freeDeliveryThreshold
			? 0
			: this.minDeliveryCost;

		const displayPrice = resolvedDeliveryPrice + this.deliveryPrice;
		deliveryPriceEle.innerText = displayPrice > 0 ? Core.formatCurrency(displayPrice) : "FREE";
	}

	/**
	 * Verify if cart contains digital items
	 * @returns "mixed" | "digital" | "physical"
	 */
	getCartType() {
		// returns true if cart mixes deliverable and no deliverable items
		const itemTypes = Array.from(
			new Set(window.cart.cartObject?.lines.nodes.map((item) => item.merchandise.product.productType) || [])
		);

		const hasDigitalItems = itemTypes.some((item) => this.digitalTypes.includes(item));
		const otherItems = itemTypes.filter((item) => !this.digitalTypes.includes(item));

		if (hasDigitalItems) return otherItems.length > 0 ? "mixed" : "digital";
		return "physical";
	}

	/**
	 * Obtain cart subtotal excluding digital items
	 * @returns float
	 */
	getPhysicalSubtotal() {
		return (
			window.cart.cartObject?.lines.nodes.reduce((acc, item) => {
				const { productType } = item.merchandise.product;
				if (this.digitalTypes.includes(productType)) return acc;
				return acc + parseFloat(item.merchandise.price.amount) * item.quantity;
			}, 0) || 0
		);
	}

	updateTotal() {
		const cart = window?.cart?.cartObject || false;
		if (!cart) return;

		const totalEle = this.container.querySelector("[data-cart-total]");
		const discountEle = this.container.querySelector("[data-cart-discounts]");

		const { discountAllocations, appliedGiftCards } = window?.cart?.cartObject || {};

		discountEle.classList.add("hidden");
		discountEle.innerHTML = "Discounts";

		const renderDiscountItem = (discount) => {
			const disValue = discount.discountedAmount || discount.amountUsed;
			const disCode = discount.code || discount.lastCharacters || "DISCOUNT";
			const codeRepeated = discountEle.querySelector(`[data-discount-code = '${disCode.toUpperCase()}']`);
			if (codeRepeated) {
				const appliedAmount = codeRepeated.getAttribute("data-discount-amount");
				const totalAmount = parseFloat(appliedAmount, 10) + parseFloat(disValue.amount, 10);
				codeRepeated.setAttribute("data-discount-amount", totalAmount);
				codeRepeated.innerText = `- ${Core.formatCurrency(totalAmount)}`;
			} else {
				const discountItemsWrapper = document.createElement("div");
				const discountItem = document.createElement("div");
				const discountCode = document.createElement("div");
				const discountAmount = document.createElement("div");
				discountItemsWrapper.classList.add("flex", "flex-col");
				discountItem.classList.add("flex", "justify-between");
				discountCode.classList.add("opacity-50", "uppercase", "text-xs");
				discountAmount.setAttribute("data-discount-code", disCode.toUpperCase());
				discountAmount.setAttribute("data-discount-amount", disValue.amount);

				discountCode.innerText = discount.lastCharacters ? `...${disCode.toUpperCase()}` : disCode.toUpperCase();
				discountAmount.innerText = `- ${Core.formatCurrency(disValue.amount)}`;

				discountItem.append(discountCode, discountAmount);
				discountItemsWrapper.appendChild(discountItem);
				discountEle.appendChild(discountItemsWrapper);
			}
		};

		discountAllocations.forEach(renderDiscountItem);
		appliedGiftCards.forEach(renderDiscountItem);

		if (discountAllocations.length || appliedGiftCards.length) discountEle.classList.remove("hidden");

		totalEle.innerText = Core.formatCurrency(window.cart.total + this.deliveryPrice);
	}

	renderCartItem(cartItem) {
		const { merchandise, quantity, discountAllocations } = cartItem;
		const { image, product, title, price, quantityAvailable } = merchandise;
		const compareAtAmount = merchandise.compareAtPrice?.amount;
		// Only treat compareAt as a genuine markdown when it's actually higher than the current price -
		// bad merchandising data has previously set compareAt equal to or below price.
		const hasCompareAt = compareAtAmount != null && Number(compareAtAmount) > Number(price.amount);

		const cartItemNode = this.itemTemplate.content.cloneNode(true);
		const cartItemBox = cartItemNode.querySelector("div");
		const imageEle = cartItemNode.querySelector("[data-cart-item-image]");
		imageEle.setAttribute("src", image.url);
		imageEle.setAttribute("alt", product.title);

		const titleEle = cartItemNode.querySelector("[data-cart-item-title]");
		titleEle.innerText = product.title;
		titleEle.setAttribute("href", `/products/${product.handle}/`);

		const variantTitleEle = cartItemNode.querySelector("[data-cart-item-variant-title]");
		if (title !== "Default Title") variantTitleEle.innerText = title;

		const priceEle = cartItemNode.querySelector("[data-cart-item-price]");
		priceEle.innerText = Core.formatCurrency(price.amount * quantity);

		if (hasCompareAt) {
			const comparePriceEle = cartItemNode.querySelector("[data-cart-compare-at-price]");
			comparePriceEle.innerText = Core.formatCurrency(compareAtAmount * quantity);
		}

		discountAllocations.forEach((discount) => {
			if (discount.discountedAmount.amount > 0) {
				const discountPriceEle = document.createElement("p");
				const discountTitleEle = document.createElement("p");
				discountPriceEle.classList.add("font-bold", "text-black", "text-center");
				discountTitleEle.classList.add("text-[10px]", "text-grey-900");
				discountTitleEle.innerText = discount.code?.toUpperCase() || discount.title.toUpperCase();
				discountPriceEle.innerText = Core.formatCurrency(price.amount * quantity - discount.discountedAmount.amount);

				if (hasCompareAt) {
					// There's already a struck-through compare-at price - don't also strike through the sale price
					priceEle.classList.add("hidden");
				} else {
					priceEle.classList.add("line-through", "opacity-50");
				}
				priceEle.parentElement.appendChild(discountPriceEle);
				variantTitleEle.parentElement.appendChild(discountTitleEle);
			}
		});

		// BOGOS gifts are added and removed by our cart API as the cart qualifies. Only a picked gift can be
		// removed, BOGOS then offers the choices again, an automatic one would just be added back
		if (product.tags?.includes("bogos-gift")) {
			titleEle.removeAttribute("href");
			const discounts = discountAllocations.reduce(
				(total, { discountedAmount }) => total + Number(discountedAmount.amount),
				0
			);
			const giftLabel = price.amount * quantity - discounts > 0 ? "Discounted gift" : "Free gift";
			variantTitleEle.innerText = quantity > 1 ? `${quantity}x ${giftLabel}` : giftLabel;
			const removeGiftEle = cartItemNode.querySelector("[data-cart-item-remove]");
			if (cartItem.attributes?.some(({ key }) => key === PICKED_GIFT_ATTRIBUTE)) {
				removeGiftEle.addEventListener("click", (e) => this.handleCartItemUpdate(e, cartItem.id, 0, cartItemBox));
			} else {
				removeGiftEle.remove();
			}
			cartItemNode.querySelector("[data-cart-item-quantity]").parentNode.remove();
			this.items.appendChild(cartItemNode);
			return;
		}

		const quantityEle = cartItemNode.querySelector("[data-cart-item-quantity]");

		if (quantity === 0 || quantityAvailable === 0) {
			priceEle.classList.add("opacity-50");
			titleEle.classList.add("opacity-50");
			imageEle.classList.add("opacity-50");
			quantityEle.parentNode.classList.add("text-alert", "text-sm");
			quantityEle.parentNode.innerText = "Sold out";
		} else {
			const selectedQuantity = quantityEle.querySelector(`option[value="${quantity}"]`);
			selectedQuantity.setAttribute("selected", "");

			quantityEle.addEventListener("change", (e) => {
				this.handleCartItemUpdate(e, cartItem.id, e.target.value, cartItemBox);
			});

			const increaseQuantityEle = cartItemNode.querySelector("[data-cart-item-increase-quantity]");
			increaseQuantityEle.addEventListener("click", (e) => {
				this.handleCartItemUpdate(e, cartItem.id, quantity + 1, cartItemBox);
			});

			const decreaseQuantityEle = cartItemNode.querySelector("[data-cart-item-decrease-quantity]");
			decreaseQuantityEle.addEventListener("click", (e) =>
				this.handleCartItemUpdate(e, cartItem.id, quantity - 1, cartItemBox)
			);
		}
		const removeItemEle = cartItemNode.querySelector("[data-cart-item-remove]");
		removeItemEle.addEventListener("click", (e) => this.handleCartItemUpdate(e, cartItem.id, 0, cartItemBox));

		this.items.appendChild(cartItemNode);
	}

	async handleAddDiscountCode() {
		const discountError = this.discountContainer.querySelector(".discount-error");
		const input = this.discountContainer.querySelector(".discount-form input");
		const code = input.value;
		discountError.classList.add("hidden");

		const result = await super.addDiscountCode(code);

		const isGiftCode = code.split(" ").length > 3;

		if (isGiftCode) {
			if (!result.appliedGiftCards.find((dc) => dc.lastCharacters.toLowerCase() === code.slice(-4).toLowerCase())) {
				discountError.classList.remove("hidden");
				return;
			}
		} else if (!result.discountCodes.find((dc) => dc.code.toLowerCase() === code.toLowerCase() && dc.applicable)) {
			discountError.classList.remove("hidden");
			return;
		}

		this.updateCartContents();
	}

	handleDiscountForm() {
		const applyBtn = this.discountForm.querySelector("button");
		this.showDiscountFormBtn.addEventListener("click", () => {
			this.showDiscountFormBtn.classList.add("hidden");
			this.discountForm.classList.remove("hidden");
		});
		applyBtn.addEventListener("click", this.handleAddDiscountCode.bind(this));
	}

	handleCartDrawerControllers() {
		this.cartDrawerControllers.forEach((controller) =>
			controller.addEventListener("click", this.toggleCartDrawer.bind(this))
		);
		this.overlay.addEventListener("click", this.toggleCartDrawer.bind(this));
	}

	updateMinimumOrderValues() {
		this.thresholdAmount.forEach((ele) => {
			ele.innerText = Core.formatCurrency(this.minimumOrderValue, false);
		});
	}

	// Cart item update methods

	async handleCartItemUpdate(e, cartItemId, newQuantity, node) {
		node.classList.add("opacity-50");
		const quantity = Number(newQuantity);
		if (quantity <= 0) {
			await this.removeItemsCart({
				cartId: window.cart.cartId,
				site: this.siteId,
				lineItemIds: [cartItemId],
			});
			node.classList.remove("opacity-50");
		} else if (!cartItemId.includes("CartLine")) {
			await this.addItemsCart(e, {
				cartId: window.cart.cartId,
				site: this.siteId,
				items: [{ merchandiseId: cartItemId, quantity: quantity }],
			});
		} else {
			await this.updateItemsCart({
				cartId: window.cart.cartId,
				site: this.siteId,
				items: [{ id: cartItemId, quantity: quantity }],
			});
		}

		this.updateCartContents();
	}

	handleGoToCheckout() {
		this.checkoutBtn.addEventListener("click", (e) => {
			const isDisabled = this.checkoutBtn.getAttribute("aria-disabled") === "true";
			if (isDisabled) {
				e.preventDefault();
				const promptVisible = this.thresholdPrompt.getAttribute("hidden") === null;
				if (promptVisible) {
					this.thresholdPrompt.classList.add("animate-shake");
					this.thresholdPrompt.addEventListener(
						"animationend",
						() => {
							this.thresholdPrompt.classList.remove("animate-shake");
						},
						{ once: true }
					);
				} else {
					this.thresholdPrompt.removeAttribute("hidden");
					this.thresholdPrompt.classList.add("animate-fade-in");
					this.thresholdPrompt.addEventListener(
						"animationend",
						() => {
							this.thresholdPrompt.classList.remove("animate-fade-in");
						},
						{ once: true }
					);
				}
				e.stopImmediatePropagation();
				window.addEventListener(
					"click",
					() => {
						this.thresholdPrompt.classList.add("animate-fade-out");
						this.thresholdPrompt.addEventListener(
							"animationend",
							() => {
								this.thresholdPrompt.setAttribute("hidden", "hidden");
								this.thresholdPrompt.classList.remove("animate-fade-out");
							},
							{ once: true }
						);
					},
					{ once: true }
				);
			}
		});
	}

	/**
	 * Cart upsell logic
	 *
	 * Tip: a helper script used for testing rules can be found in `../support-files/cart-upsell-rule-tester`
	 * It Provides sample carts and a rule evaluator to validate changes safely.
	 * */
	updateCartUpsell() {
		this.upsellContainer.classList.add("hidden");

		if (!this.cartItems || !this.cartUpsellItems) return;

		const cartClass = this.classifyCart(this.cartItems);
		const upsellClass =
			this.cartUpsellItems.find((item) => item.key === cartClass) ||
			this.cartUpsellItems.find((item) => item.key === "fallback");

		if (!upsellClass || !upsellClass.items?.length) return;

		this.upsellItems = upsellClass.items.filter(
			(upsell) => !this.cartItems?.some((cartItem) => cartItem.merchandise.id === upsell.id) && upsell.availableForSale
		);

		if (!this.upsellItems.length) return;

		this.recreateCarousel();
	}

	recreateCarousel() {
		this.upsellContainer.classList.remove("hidden");
		this.carouselContainer.innerHTML = "";

		const carousel = this.carouselTemplate.content.cloneNode(true);
		const carouselEle = carousel.querySelector("[data-carousel]");
		this.carouselItemsContainer = carousel.querySelector("[data-movable]");

		this.upsellItems.forEach(this.renderCartUpsellItem.bind(this));

		this.carouselContainer.appendChild(carousel);

		// eslint-disable-next-line no-new
		if (carouselEle) new Carousel(carouselEle, { autoAdvance: false, playOnce: false, loop: false, moveScreen: true });
	}

	renderCartUpsellItem(cartItem) {
		const { image, product, price, id, title } = cartItem;
		const itemFragment = this.upsellItemTemplate.content.cloneNode(true);
		const itemNode = itemFragment.querySelector("div");

		const imageEle = itemNode.querySelector("[data-cart-upsell-image]");
		const titleEle = itemNode.querySelector("[data-cart-upsell-title]");
		const priceEle = itemNode.querySelector("[data-cart-upsell-price]");
		const variantEle = itemNode.querySelector("[data-cart-upsell-variant]");
		const addCartUpsellButton = itemNode.querySelector("[data-cart-upsell-add-to-cart]");

		imageEle.setAttribute("src", image.url);
		imageEle.setAttribute("alt", product.title);

		titleEle.innerText = product.title;
		titleEle.setAttribute("href", `/products/${product.handle}/`);

		priceEle.innerText = Core.formatCurrency(price.amount);

		if (title !== "Default Title") variantEle.innerText = title;

		addCartUpsellButton.addEventListener("click", (e) => this.handleCartItemUpdate(e, id, 1, itemNode));

		this.carouselItemsContainer.appendChild(itemNode);
	}

	evaluateRule(clause, ctx) {
		if (clause.AND) return clause.AND.every((c) => this.evaluateRule(c, ctx));
		if (clause.OR) return clause.OR.some((c) => this.evaluateRule(c, ctx));

		if (clause.type) {
			const availableTypes = ["plants", "pots", "accessories", "bundles"];
			return availableTypes.every((key) => {
				const actual = ctx.types[key] || 0;
				const required = clause.type[key] || 0;
				return actual === required;
			});
		}

		if (clause.environment) {
			const availableEnvs = ["plantsIndoor", "potsIndoor", "plantsOutdoor", "potsOutdoor"];
			return availableEnvs.every((key) => {
				const actual = ctx.aggregated[key] || 0;
				const required = clause.environment[key] || 0;
				return actual === required;
			});
		}

		if (clause.valueBelow !== undefined) return ctx.totalValue < clause.valueBelow;
		if (clause.valueAtLeast !== undefined) return ctx.totalValue >= clause.valueAtLeast;
		if (clause.indoorShareAbove !== undefined) return ctx.indoorShare > clause.indoorShareAbove;
		if (clause.outdoorShareAbove !== undefined) return ctx.outdoorShare > clause.outdoorShareAbove;

		return true;
	}

	buildCartContext(cart) {
		const getProductInfo = (line) => {
			const p = line.merchandise.product;
			const type = p.productType?.toLowerCase();

			const isPlant = type === "plants" || type === "plant";
			const isPot = type === "pots" || type === "pot";
			const isBundle = type === "bundles" || type === "bundle";
			const isAccessory = type === "accessories";

			const tags = p.tags?.map((t) => t.toLowerCase()) || [];
			const indoor = tags.includes("environment:indoor");
			const outdoor = tags.includes("environment:outdoor");

			return {
				isPlant,
				isPot,
				isBundle,
				isAccessory,
				indoor,
				outdoor,
				price: Number(line.cost.totalAmount.amount),
			};
		};

		const types = {
			plants: 0,
			pots: 0,
			bundles: 0,
			accessories: 0,
		};

		const aggregated = {
			plantsIndoor: 0,
			potsIndoor: 0,
			plantsOutdoor: 0,
			potsOutdoor: 0,
		};

		let indoorValue = 0;
		let outdoorValue = 0;
		let totalValue = 0;

		// eslint-disable-next-line no-restricted-syntax
		for (const line of cart) {
			const info = getProductInfo(line);
			const qty = line.quantity;
			const { price } = info;

			totalValue += price;

			// type counts
			if (info.isPlant) types.plants += qty;
			if (info.isPot) types.pots += qty;
			if (info.isBundle) types.bundles += qty;
			if (info.isAccessory) types.accessories += qty;

			// indoor/outdoor
			if (info.isPlant && info.indoor) aggregated.plantsIndoor += qty;
			if (info.isPot && info.indoor) aggregated.potsIndoor += qty;

			if (info.isPlant && info.outdoor) aggregated.plantsOutdoor += qty;
			if (info.isPot && info.outdoor) aggregated.potsOutdoor += qty;

			if (info.indoor) indoorValue += price;
			if (info.outdoor) outdoorValue += price;
		}

		return {
			types,
			aggregated,
			totalValue,
			indoorShare: indoorValue / totalValue,
			outdoorShare: outdoorValue / totalValue,
		};
	}

	classifyCart(cartLines) {
		const ctx = this.buildCartContext(cartLines);
		// eslint-disable-next-line no-restricted-syntax
		for (const rule of this.cartUpsellRules) {
			if (!rule.conditions || Object.keys(rule.conditions).length === 0) return true;
			const result = Object.entries(rule.conditions).every(([k, v]) => this.evaluateRule({ [k]: v }, ctx));
			if (result) return rule.id;
		}

		return "fallback";
	}

	init() {
		this.handleCartDrawerControllers();
		this.handleGoToCheckout();
		this.handleDiscountForm();

		const params = new URL(document.location).searchParams;
		if (params.size !== 0 && typeof params.get("cartOpen") === "string") {
			setTimeout(this.toggleCartDrawer.bind(this), 2400);
		}
	}
}

// eslint-disable-next-line no-new
const cart = new Cart();
cart.init();
